import { useState } from "react";
import { motion } from "framer-motion";

const OPTIONS = [
  { id: "small", label: "Small", desc: "Everyday lookups", level: 1 },
  { id: "medium", label: "Medium", desc: "A closer look", level: 2 },
  { id: "large", label: "Large", desc: "More room to explore", level: 3 },
];

const Meter = ({ level, activeLevel }) => (
  <span className="flex items-end gap-1" aria-hidden="true">
    {[1, 2, 3].map((i) => (
      <i
        key={i}
        className={`w-1.5 rounded-sm transition-all duration-300 ${
          activeLevel >= i ? "bg-[#ff8a4c]" : "bg-[#233c4d]/70"
        }`}
        style={{ height: `${6 + i * 4}px` }}
      />
    ))}
    <span className="sr-only">{`Effort level ${level} of 3`}</span>
  </span>
);

export const EffortCard = () => {
  const [effort, setEffort] = useState("medium");
  const activeLevel = OPTIONS.find((o) => o.id === effort).level;

  return (
    <section
      id="effort"
      data-testid="effort-section"
      className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-xl text-center"
      >
        <span className="eyebrow">Search effort</span>
        <h2 className="mt-4 text-3xl font-medium tracking-tight text-[#f3f5f3] md:text-4xl lg:text-5xl">
          A quick look.
          <br />
          <span className="text-[#889296]">Or a closer one.</span>
        </h2>
        <p className="mt-4 text-sm text-[#889296] md:text-base">
          Not every question needs the same effort. Give a bigger question more
          room to explore.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        data-testid="effort-card"
        className="mx-auto mt-12 max-w-md rounded-3xl border border-[#233c4d]/40 bg-[#10191f] p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm font-medium text-[#edf2f4]">
            wondersearch-1.1
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#889296]">
            Choose an effort for each search
          </span>
        </div>
        <ul className="mt-6 space-y-2">
          {OPTIONS.map((opt) => (
            <li key={opt.id}>
              <button
                type="button"
                aria-pressed={effort === opt.id}
                data-testid={`effort-option-${opt.id}`}
                onClick={() => setEffort(opt.id)}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-left transition-all duration-200 ${
                  effort === opt.id
                    ? "border-[#ff8a4c]/50 bg-[#111813]"
                    : "border-[#233c4d]/30 bg-transparent hover:border-[#233c4d]/70"
                }`}
              >
                <span className="flex items-center gap-3">
                  <Meter level={opt.level} activeLevel={effort === opt.id ? opt.level : 0} />
                  <span>
                    <strong className="block text-sm font-semibold text-[#f3f5f3]">
                      {opt.label}
                    </strong>
                    <span className="block text-xs text-[#889296]">{opt.desc}</span>
                  </span>
                </span>
                <span
                  className={`h-2 w-2 rounded-full transition-colors duration-200 ${
                    effort === opt.id ? "bg-[#ff8a4c]" : "bg-[#233c4d]/60"
                  }`}
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>
      </motion.div>

      <div className="mx-auto mt-6 flex max-w-md items-center justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-[#889296]">
        <span>Illustration of search effort.</span>
        <span>Small · Medium · Large</span>
      </div>
    </section>
  );
};
